import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { useAuth } from "./contexts/AuthContext";
import { useIdleTimeout } from "./hooks/useIdleTimeout";
import Layout from "./components/Layout/Layout";
import LandingPage from "./pages/LandingPage";
import NotFound from "./pages/NotFound";
import Tombstone from "./pages/Tombstone";
import LoginPage from "./pages/Auth/LoginPage";
import JobSeekerSignup from "./pages/Auth/JobSeekerSignup";
import HRSignup from "./pages/Auth/HRSignup";
import VerifyEmail from "./pages/Auth/VerifyEmail";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import ResetPassword from "./pages/Auth/ResetPassword";
import Features from "./pages/Features";
import JobsList from "./pages/Jobs/JobsList";
import JobDetail from "./pages/Jobs/JobDetail";
import Apply from "./pages/Jobs/Apply";
import Security from "./pages/Security";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Status from "./pages/Status";
import HelpCenter from "./pages/HelpCenter";
import TermsAndConditions from "./pages/Legal/TermsAndConditions";
import PrivacyPolicy from "./pages/Legal/PrivacyPolicy";
import JobSeekerProfile from "./pages/JobSeeker/Profile";
import JobSeekerResume from "./pages/JobSeeker/Resume";
import JobSeekerApplications from "./pages/JobSeeker/Applications";
import HRProfile from "./pages/HR/Profile";
import CreateJob from "./pages/HR/CreateJob";
import MyJobs from "./pages/HR/MyJobs";
import JobManage from "./pages/HR/JobManage";
import Applicants from "./pages/HR/Applicants";
import ApplicantDetail from "./pages/HR/ApplicantDetail";
import StatusList from "./pages/HR/StatusList";
import Dashboard from "./pages/Admin/Dashboard";
import Users from "./pages/Admin/Users";
import UserDetail from "./pages/Admin/UserDetail";
import Requests from "./pages/Admin/Requests";
import Companies from "./pages/Admin/Companies";
import CompanyDetail from "./pages/Admin/CompanyDetail";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import CompanyGate from "./components/Auth/CompanyGate";
import CompanyIncompleteNotice from "./components/Company/CompanyIncompleteNotice";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "react-hot-toast";

/*
 * Editorial page-transition wrapper. The `key` on location.pathname
 * forces React to re-mount the inner subtree on every route change,
 * which replays the .velaigal-page-enter CSS animation defined in
 * index.css. The wrapper sits INSIDE AppRoutes so the idle-timeout hook
 * and AuthProvider above don't re-mount on navigation.
 *
 * Per the design system §1.6: page transitions are 240ms with an 8px
 * vertical lift and ease-out-expo. Reduced-motion users get an instant
 * cut via the @media block in index.css.
 */
const PageTransition: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return (
    <div key={location.pathname} className="velaigal-page-enter">
      {children}
    </div>
  );
};

/* Every /hr/* page carries the same wrappers; naming them once keeps the no-company branch from
   being forgotten on a route added later. CompanyGate sits between the role check and the
   Layout: an HR session with no company, or one whose employer was suspended, gets the
   explanation instead of chrome whose every link 403s. CompanyIncompleteNotice is INSIDE the
   Layout because the third state does not block — an incomplete company can do everything except
   publish a job and generate its tasks, so it gets a line above the page, not a wall. */
const HrRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute userType="hr">
    <CompanyGate>
      <Layout userType="hr">
        <CompanyIncompleteNotice />
        {children}
      </Layout>
    </CompanyGate>
  </ProtectedRoute>
);

const AppRoutes: React.FC = () => {
  const { user } = useAuth();

  // Enable idle timeout only for authenticated users
  // Auto-logout after 15 minutes of inactivity
  useIdleTimeout({
    timeout: 15 * 60 * 1000, // 15 minutes
  });

  // Determine what to show when users access the root path
  const getRootRedirect = () => {
    // If the visitor is not authenticated, show the public landing page
    if (!user) return <LandingPage />;

    // Otherwise, take them straight to the appropriate dashboard
    switch (user.userType) {
      case "jobseeker":
        return <Navigate to="/job-seeker/jobs" replace />;
      case "hr":
        return <Navigate to="/hr/jobs" replace />;
      case "admin":
        return <Navigate to="/admin" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  };

  return (
    <PageTransition>
      <Routes>
        {/* Root redirect */}
        <Route path="/" element={getRootRedirect()} />

        {/* Public Routes */}
        <Route path="/jobs" element={<JobsList />} />
        <Route path="/jobs/:id" element={<JobDetail />} />
        <Route
          path="/jobs/:id/apply"
          element={
            <ProtectedRoute userType="jobseeker">
              <Layout userType="jobseeker">
                <Apply />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route path="/features" element={<Features />} />
        <Route path="/security" element={<Security />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/status" element={<Status />} />
        <Route path="/help-center" element={<HelpCenter />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />

        {/* Auth Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup/jobseeker" element={<JobSeekerSignup />} />
        <Route path="/signup/hr" element={<HRSignup />} />
        <Route path="/verify" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Job Seeker Routes */}
        <Route path="/job-seeker" element={<Navigate to="/job-seeker/jobs" replace />} />
        {/* The job board is now a fully public page — this redirects any
            bookmarked/old link straight there. */}
        <Route path="/job-seeker/jobs" element={<Navigate to="/jobs" replace />} />
        <Route
          path="/job-seeker/applications"
          element={
            <ProtectedRoute userType="jobseeker">
              <Layout userType="jobseeker">
                <JobSeekerApplications />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/job-seeker/profile"
          element={
            <ProtectedRoute userType="jobseeker">
              <Layout userType="jobseeker">
                <JobSeekerProfile />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/job-seeker/resume"
          element={
            <ProtectedRoute userType="jobseeker">
              <Layout userType="jobseeker">
                <JobSeekerResume />
              </Layout>
            </ProtectedRoute>
          }
        />
        {/* Legacy bookmark from the old assessment flow — public tombstone, not gated */}
        <Route path="/job-seeker/tests" element={<Tombstone />} />

        {/* HR Routes */}
        <Route path="/hr" element={<Navigate to="/hr/jobs" replace />} />
        <Route
          path="/hr/create-job"
          element={<HrRoute><CreateJob /></HrRoute>}
        />
        <Route
          path="/hr/jobs/:id/edit"
          element={<HrRoute><CreateJob /></HrRoute>}
        />
        <Route
          path="/hr/jobs"
          element={<HrRoute><MyJobs /></HrRoute>}
        />
        <Route
          path="/hr/jobs/:id"
          element={<HrRoute><JobManage /></HrRoute>}
        />
        <Route
          path="/hr/jobs/:id/applicants"
          element={<HrRoute><Applicants /></HrRoute>}
        />
        <Route
          path="/hr/applications/:id"
          element={<HrRoute><ApplicantDetail /></HrRoute>}
        />
        <Route
          path="/hr/shortlisted"
          element={<HrRoute><StatusList status="shortlisted" /></HrRoute>}
        />
        <Route
          path="/hr/rejected"
          element={<HrRoute><StatusList status="rejected" /></HrRoute>}
        />
        <Route
          path="/hr/profile"
          element={<HrRoute><HRProfile /></HrRoute>}
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute userType="admin">
              <Layout userType="admin">
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute userType="admin">
              <Layout userType="admin">
                <Users />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users/:id"
          element={
            <ProtectedRoute userType="admin">
              <Layout userType="admin">
                <UserDetail />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/companies"
          element={
            <ProtectedRoute userType="admin">
              <Layout userType="admin">
                <Companies />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/companies/:id"
          element={
            <ProtectedRoute userType="admin">
              <Layout userType="admin">
                <CompanyDetail />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/requests"
          element={
            <ProtectedRoute userType="admin">
              <Layout userType="admin">
                <Requests />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Shared test links — assessments are discontinued */}
        <Route path="/test/share/*" element={<Tombstone />} />

        {/* Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </PageTransition>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <div className="min-h-screen bg-cream text-ink">
            <ScrollToTop />
            <AppRoutes />
            <Toaster
              position="top-center"
              toastOptions={{
                duration: 3000,
                style: {
                  borderRadius: "6px",
                  background: "#FBF8F1",
                  border: "1px solid #1A1614",
                  color: "#1A1614",
                  fontFamily: '"Instrument Sans", system-ui, sans-serif',
                  fontSize: "0.875rem",
                  boxShadow: "0 8px 24px -4px rgba(26, 22, 20, 0.08)",
                },
                success: {
                  iconTheme: {
                    primary: "#2D6A4F",
                    secondary: "#FBF8F1",
                  },
                },
                error: {
                  iconTheme: {
                    primary: "#991B1B",
                    secondary: "#FBF8F1",
                  },
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
